import React, { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";

/**
 * BariCanoneSimulator.tsx
 * Plug-and-play React + TypeScript component that reproduces the provided HTML page
 * "Simulatore Canone Concordato – Bari (Accordo 09/09/2024)" with the same UI/logic.
 *
 * Notes for integration:
 * - No external CSS deps: styles are inlined to mimic the original page.
 * - Works with plain React/TS projects; looks fine with Bootstrap present as well.
 * - You can pass a custom `config` via props; otherwise it uses the embedded JSON below.
 */

// ===================== Types ===================== //

type Bands = { A: [number, number]; B: [number, number]; C: [number, number] };

type Zone = {
  id: number;
  name: string;
  bands: Bands;
};

type Config = {
  meta: {
    title: string;
    version: string;
    currency: string;
    changelog?: string[];
    sources?: string[];
  };
  zones: Zone[];
  params: string[];
  ui: {
    help: Record<string, string>;
    min_requirements_help: string[];
  };
  rules: {
    min_requirements: {
      description: string;
      items: string[];
      non_normal_band: "A" | "B" | "C";
    };
    via_mapping: {
      description: string;
      normal_dwelling: Array<
        | { if_params_lte: number; band: "A" | "B" | "C"; position: PositionKey }
        | { if_params_eq: number; band: "A" | "B" | "C"; position: PositionKey }
        | { if_params_between: [number, number]; band: "A" | "B" | "C"; position: PositionKey }
        | { if_params_gte: number; band: "A" | "B" | "C"; position: PositionKey }
      >;
      non_normal_dwelling: Array<
        | { if_params_lte: number; position: PositionKey }
        | { if_params_eq: number; position: PositionKey }
        | { if_params_between: [number, number]; position: PositionKey }
        | { if_params_gte: number; position: PositionKey }
      >;
      position_to_fraction: Record<PositionKey, number>;
    };
    adjustments: {
      furnishing: {
        partial: {
          if_band_A: { multiplier: number };
          if_band_not_A: { promote_to: "A" | "B" | "C"; multiplier: number };
        };
        total: {
          if_band_A: { multiplier: number };
          if_band_not_A: { promote_to: "A" | "B" | "C"; multiplier: number };
        };
      };
      semi_basement: { multiplier: number };
      top_floor_no_elevator_over_3rd: { multiplier: number };
      attic: { multiplier: number };
      eco_upgrades: { none: number; one: number; two_or_more: number };
      seismic_bonus: { multiplier: number };
      energy_class: { D_E_F: number; B_C: number; A_or_higher: number };
      "students_near_university_≤800m": { multiplier: number };
    };
    contract_modifiers: {
      ordinario: { duration_years: { "3": number; "4": number; "5": number; "6_or_more": number } };
      transitorio: { max_increase_percent: number };
      studenti: { duration_months: { "≥24": number; "≥36": number } };
    };
    guarantee_reduction: { "third_party_guarantee_months_≥12": number };
  };
};

type PositionKey = "min" | "low" | "low_mid" | "mid" | "upper_mid" | "high" | "max";

// ===================== Default Config (embedded) ===================== //

const defaultConfig: Config = {
  meta: {
    title: "Simulatore Canone Concordato – Bari (Accordo 13/09/2024)",
    version: "1.1.0",
    currency: "EUR/mese",
    changelog: ["v1.1.0: aggiunte note/tooltip esplicative per ogni parametro e requisiti minimi."],
    sources: ["Accordo territoriale Bari 13/09/2024 – FEDERPROPRIETÀ, UNIONCASA, FEDER.CASA, FE.N.I.T., ANIA"],
  },
  zones: [
    { id: 1, name: "Città Vecchia", bands: { A: [5.1, 6.5], B: [3.9, 5.0], C: [2.7, 3.8] } },
    { id: 2, name: "Murat, 1/2 Libertà, 1/2 Madonella", bands: { A: [6.5, 8.4], B: [5.0, 6.4], C: [3.3, 4.9] } },
    { id: 3, name: "1/2 Madonella, Japigia ed espansione", bands: { A: [5.3, 6.5], B: [4.3, 5.2], C: [3.3, 4.2] } },
    { id: 4, name: "Carrassi, Picone, San Pasquale, Poggiofranco", bands: { A: [6.8, 8.7], B: [5.1, 6.7], C: [3.5, 5.0] } },
    { id: 5, name: "San Paolo, Stanic", bands: { A: [4.8, 5.7], B: [3.9, 4.7], C: [3.4, 3.8] } },
    { id: 6, name: "1/2 Libertà, San Girolamo", bands: { A: [4.9, 6.0], B: [3.9, 4.8], C: [2.9, 3.8] } },
    { id: 7, name: "Torre a Mare, San Giorgio", bands: { A: [4.5, 6.2], B: [3.6, 4.4], C: [2.6, 3.5] } },
    { id: 8, name: "Carbonara, Ceglie del Campo, Loseto", bands: { A: [4.3, 6.0], B: [3.4, 4.2], C: [2.7, 3.3] } },
    { id: 9, name: "Palese Macchie, Santo Spirito", bands: { A: [5.7, 6.5], B: [4.5, 5.6], C: [3.3, 4.4] } },
  ],
  params: [
    "Box (uso esclusivo)",
    "Posto auto esclusivo",
    "Wallbox EV nel box/posto auto",
    "Posto auto non esclusivo",
    "Balcone/Terrazza",
    "Cantina/Soffitta",
    "Giardino esclusivo",
    "Verde comune",
    "Cortile comune",
    "Ascensore",
    "Cassaforte",
    "Senza barriere/ausili",
    "Stabile ≤4 piani (max 3 appart./piano)",
    "Ultimato/ristrutturato ultimi 10 anni",
    "Porta blindata",
    "Allarme",
    "Videocitofono/Videosorveglianza",
    "Cucina abitabile (≥9 mq)",
    "Servizio igienico 4 elementi o doppi servizi",
    "Antenna terrestre/sat",
    "Clima in >50% alloggio",
    "Infissi isolanti",
    "Riscaldamento autonomo",
    "Generatore evoluto (pompa/ibrido/solare)",
    "Fotovoltaico + accumulo",
    "Cappotto termico",
    "Fibra FTTH",
    "Domotica >50% ambienti",
    "Vicino colonnina EV/Car-Sharing (≤250 m)",
    "Bus urbano a ≤500 m",
  ],
  ui: {
    help: {
      "Box (uso esclusivo)": "Spunta se l’immobile dispone di box/garage di pertinenza ad uso esclusivo del conduttore.",
      "Posto auto esclusivo": "Presenza di posto auto assegnato (coperto o scoperto) ad uso esclusivo.",
      "Wallbox EV nel box/posto auto": "Colonnina/wallbox per ricarica veicoli elettrici nel box/posto auto esclusivo.",
      "Posto auto non esclusivo": "Aree di sosta condominiali condivise, senza assegnazione fissa.",
      "Balcone/Terrazza": "Almeno uno tra balcone, veranda, terrazza o terrazzo a livello pertinenti all’unità.",
      "Cantina/Soffitta": "Cantina, soffitta o locale deposito di pertinenza dell’alloggio.",
      "Giardino esclusivo": "Giardino/area verde privati ad uso esclusivo del conduttore.",
      "Verde comune": "Spazi verdi condominiali fruibili (aiuole, corti a verde, ecc.).",
      "Cortile comune": "Cortile condominiale pavimentato o misto ad uso comune.",
      Ascensore: "Edificio dotato di ascensore funzionante che serve il piano dell’alloggio.",
      Cassaforte: "Cassaforte a muro o d’arredo installata nell’unità.",
      "Senza barriere/ausili": "Accessibilità: rampe/ausili/ascensore, assenza di barriere per l’accesso all’unità.",
      "Stabile ≤4 piani (max 3 appart./piano)": "Edificio con al massimo 4 piani e non più di 3 unità per piano.",
      "Ultimato/ristrutturato ultimi 10 anni":
        "Alloggio nuovo o ristrutturato integralmente (impianti/finimenti) negli ultimi 10 anni.",
      "Porta blindata": "Porta di ingresso blindata o con classe di resistenza equivalente.",
      Allarme: "Sistema di allarme/sicurezza (centrale, perimetrale, volumetrico, ecc.).",
      "Videocitofono/Videosorveglianza": "Videocitofono nell’alloggio e/o videosorveglianza condominiale/privata attiva.",
      "Cucina abitabile (≥9 mq)": "Cucina separata di almeno 9 m² con finestra apribile.",
      "Servizio igienico 4 elementi o doppi servizi":
        "Bagno con wc, bidet, lavabo, doccia/vasca (4 elementi) oppure doppi servizi (almeno uno a 4 elementi).",
      "Antenna terrestre/sat": "Antenna TV terrestre o satellitare, individuale o centralizzata, funzionante.",
      "Clima in >50% alloggio": "Impianto di climatizzazione estiva che serve più della metà dei locali.",
      "Infissi isolanti":
        "Infissi in buono stato con caratteristiche di isolamento termo-acustico (vetrocamera o equivalenti).",
      "Riscaldamento autonomo": "Impianto di riscaldamento autonomo con gestione indipendente.",
      "Generatore evoluto (pompa/ibrido/solare)":
        "Generatore autonomo evoluto (pompa di calore, ibrido, microcogenerazione, solare termico collegato).",
      "Fotovoltaico + accumulo": "Impianto fotovoltaico con sistema di accumulo fruibile dall’unità.",
      "Cappotto termico": "Isolamento termico dell’involucro (cappotto esterno/interno) su edificio o unità.",
      "Fibra FTTH": "Fibra ottica FTTH: cavo in fibra fino dentro l’alloggio.",
      "Domotica >50% ambienti":
        "Sistemi domotici che controllano almeno il 50% degli ambienti (luci, clima, tapparelle, sicurezza, ecc.).",
      "Vicino colonnina EV/Car-Sharing (≤250 m)":
        "Entro 250 m dall’ingresso: colonnine EV e/o postazioni fisse di car-sharing (distanza pedonale).",
      "Bus urbano a ≤500 m": "Entro 500 m dall’ingresso: almeno una fermata di linea bus urbana attiva.",
    },
    min_requirements_help: [
      "Allaccio idrico + fognante + impianto per cottura/ACS + impianto di riscaldamento: tutti presenti.",
      "Impianto elettrico conforme (DM 37/2008): dichiarazione di conformità o equipollente.",
      "Infissi in buono stato: serramenti senza difetti evidenti e con adeguata tenuta.",
    ],
  },
  rules: {
    min_requirements: {
      description: "Requisiti minimi per considerare l’alloggio 'normale' (altrimenti 'non normale' → base in fascia C).",
      items: [
        "Allaccio idrico + fognante + impianto per cottura/ACS + impianto di riscaldamento",
        "Impianto elettrico conforme (DM 37/2008)",
        "Infissi in buono stato",
      ],
      non_normal_band: "C",
    },
    via_mapping: {
      description: "Posizionamento iniziale del valore €/mq (VIA) in funzione del numero di parametri selezionati.",
      normal_dwelling: [
        { if_params_lte: 2, band: "C", position: "min" },
        { if_params_between: [3, 5], band: "B", position: "upper_mid" },
        { if_params_eq: 6, band: "B", position: "max" },
        { if_params_eq: 7, band: "A", position: "low" },
        { if_params_between: [8, 9], band: "A", position: "high" },
        { if_params_gte: 10, band: "A", position: "max" },
      ],
      non_normal_dwelling: [
        { if_params_lte: 1, position: "min" },
        { if_params_eq: 2, position: "low_mid" },
        { if_params_between: [3, 4], position: "upper_mid" },
        { if_params_gte: 5, position: "max" },
      ],
      position_to_fraction: {
        min: 0.0,
        low: 0.25,
        low_mid: 0.4,
        mid: 0.5,
        upper_mid: 0.8,
        high: 0.75,
        max: 1.0,
      },
    },
    adjustments: {
      furnishing: {
        partial: { if_band_A: { multiplier: 1.1 }, if_band_not_A: { promote_to: "A", multiplier: 1.0 } },
        total: { if_band_A: { multiplier: 1.2 }, if_band_not_A: { promote_to: "A", multiplier: 1.1 } },
      },
      semi_basement: { multiplier: 0.9 },
      top_floor_no_elevator_over_3rd: { multiplier: 0.9 },
      attic: { multiplier: 1.1 },
      eco_upgrades: { none: 0.0, one: 0.05, two_or_more: 0.1 },
      seismic_bonus: { multiplier: 1.1 },
      energy_class: { D_E_F: 0.05, B_C: 0.1, A_or_higher: 0.13 },
      "students_near_university_≤800m": { multiplier: 1.1 },
    },
    contract_modifiers: {
      ordinario: { duration_years: { "3": 0.0, "4": 0.05, "5": 0.07, "6_or_more": 0.12 } },
      transitorio: { max_increase_percent: 20 },
      studenti: { duration_months: { "≥24": 0.04, "≥36": 0.06 } },
    },
    guarantee_reduction: { "third_party_guarantee_months_≥12": -0.04 },
  },
};

// ===================== Helpers ===================== //

function fmtEuro(x: number) {
  return x.toFixed(2).replace(".", ",");
}

function bandValue([min, max]: [number, number], posFraction: number) {
  return min + (max - min) * posFraction;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

// ===================== Component ===================== //

export type BariCanoneSimulatorProps = {
  config?: Config;
  defaultMq?: number;
  onSubmit?: (result: any) => void;
};

const BariCanoneSimulator: React.FC<BariCanoneSimulatorProps> = ({ config, defaultMq = 0, onSubmit }) => {
  const cfg = config ?? defaultConfig;

  // UI state
  const [zonaId, setZonaId] = useState<any>(0);
  const [mq, setMq] = useState<any>(defaultMq);
  const [tipoContratto, setTipoContratto] = useState<"ordinario" | "transitorio" | "studenti" | "nessuno">("nessuno");

  const [minReqChecked, setMinReqChecked] = useState<boolean[]>(() => cfg.rules.min_requirements.items.map(() => false));
  const [paramsChecked, setParamsChecked] = useState<boolean[]>(() => cfg.params.map(() => false));

  // VINT controls
  const [arredo, setArredo] = useState<"nessuno" | "parziale" | "totale">("nessuno");
  const [semiterrato, setSemiterrato] = useState(false);
  const [oltre3senzascensore, setOltre3senzascensore] = useState(false);
  const [attico, setAttico] = useState(false);
  // Faithful to HTML: eco is a percent selector 0/5/10
  const [ecoPerc, setEcoPerc] = useState<0 | 5 | 10>(0);
  const [sisma, setSisma] = useState(false);
  const [ener, setEner] = useState<"none" | "D_E_F" | "B_C" | "A_or_higher">("none");
  const [nearUni, setNearUni] = useState(false);

  // Contract controls
  const [durataOrd, setDurataOrd] = useState<"3" | "4" | "5" | "6_or_more">("3");
  const [incTrans, setIncTrans] = useState<number>(0);
  const [durStudenti, setDurStudenti] = useState<"0" | "≥24" | "≥36">("0");
  const [garanzia, setGaranzia] = useState(false);

  const zona = useMemo(() => {
    if (zonaId === "" || zonaId === 0) return undefined;
    return cfg.zones.find((z) => z.id === zonaId);
  }, [cfg.zones, zonaId]);

  const isNormal = useMemo(() => minReqChecked.every(Boolean), [minReqChecked]);

  const paramsCount = useMemo(() => paramsChecked.filter(Boolean).length, [paramsChecked]);

  const via = useMemo(() => {
    if (!zona) return { band: "-", val: 0 };
    const mapping = cfg.rules.via_mapping;
    const posFracMap = mapping.position_to_fraction;

    let band: "A" | "B" | "C" = "B";
    let position: PositionKey = "mid";

    if (isNormal) {
      let matched = false;
      for (const rule of mapping.normal_dwelling) {
        if ("if_params_lte" in rule && paramsCount <= rule.if_params_lte) {
          band = rule.band;
          position = rule.position;
          matched = true;
          break;
        }
        if ("if_params_eq" in rule && paramsCount === rule.if_params_eq) {
          band = rule.band;
          position = rule.position;
          matched = true;
          break;
        }
        if ("if_params_between" in rule) {
          const [a, b] = rule.if_params_between as [number, number];
          if (paramsCount >= a && paramsCount <= b) {
            band = rule.band;
            position = rule.position;
            matched = true;
            break;
          }
        }
      }

      // fallback per valori >= usando type guard
      if (!matched) {
        const gteRule = mapping.normal_dwelling.find(
          (r): r is { if_params_gte: number; band: "A" | "B" | "C"; position: PositionKey } => "if_params_gte" in r
        );
        if (gteRule && paramsCount >= gteRule.if_params_gte) {
          band = gteRule.band;
          position = gteRule.position;
        }
      }
    } else {
      band = "C"; // sempre "C" per non normale
      let matched = false;
      for (const rule of mapping.non_normal_dwelling) {
        if ("if_params_lte" in rule && paramsCount <= rule.if_params_lte) {
          position = rule.position;
          matched = true;
          break;
        }
        if ("if_params_eq" in rule && paramsCount === rule.if_params_eq) {
          position = rule.position;
          matched = true;
          break;
        }
        if ("if_params_between" in rule) {
          const [a, b] = rule.if_params_between as [number, number];
          if (paramsCount >= a && paramsCount <= b) {
            position = rule.position;
            matched = true;
            break;
          }
        }
      }

      // fallback gte con type guard
      if (!matched) {
        const gteRule = mapping.non_normal_dwelling.find(
          (r): r is { if_params_gte: number; position: PositionKey } => "if_params_gte" in r
        );
        if (gteRule && paramsCount >= gteRule.if_params_gte) {
          position = gteRule.position;
        }
      }
    }

    const frac = posFracMap[position] ?? 0.0;
    const val = bandValue(zona.bands[band], frac);
    return { band, val };
  }, [cfg.rules.via_mapping, isNormal, paramsCount, zona?.bands]);

  // Calcolo range €/mq (min e max)

  const eurMqRange = useMemo(() => {
    if (!zona) return { band: "-", min: 0, max: 0 };
    const bandKey = via.band as "A" | "B" | "C";
    const [minBase, maxBase] = zona.bands[bandKey];
    const adjustments = (base: number) => {
      let e = base;
      let band = via.band;
      const adj = cfg.rules.adjustments;
      if (arredo === "parziale") {
        if (band === "A") e *= adj.furnishing.partial.if_band_A.multiplier;
        else {
          band = adj.furnishing.partial.if_band_not_A.promote_to;
          e *= adj.furnishing.partial.if_band_not_A.multiplier;
        }
      } else if (arredo === "totale") {
        if (band === "A") e *= adj.furnishing.total.if_band_A.multiplier;
        else {
          band = adj.furnishing.total.if_band_not_A.promote_to;
          e *= adj.furnishing.total.if_band_not_A.multiplier;
        }
      }
      if (semiterrato) e *= adj.semi_basement.multiplier;
      if (oltre3senzascensore) e *= adj.top_floor_no_elevator_over_3rd.multiplier;
      if (attico) e *= adj.attic.multiplier;
      e *= 1 + ecoPerc / 100;
      if (sisma) e *= adj.seismic_bonus.multiplier;
      if (ener !== "none") e *= 1 + (adj.energy_class as any)[ener];
      if (nearUni) e *= adj["students_near_university_≤800m"].multiplier;
      // contratto
      if (tipoContratto === "ordinario") {
        const delta = cfg.rules.contract_modifiers.ordinario.duration_years[durataOrd];
        e *= 1 + delta;
      } else if (tipoContratto === "transitorio") {
        e *= 1 + clamp(incTrans, 0, cfg.rules.contract_modifiers.transitorio.max_increase_percent) / 100;
      } else if (tipoContratto === "studenti") {
        const delta = cfg.rules.contract_modifiers.studenti.duration_months[durStudenti as "≥24" | "≥36"] ?? 0;
        e *= 1 + delta;
      }
      if (garanzia) e *= 1 + cfg.rules.guarantee_reduction["third_party_guarantee_months_≥12"];
      return e;
    };
    const eurMin = adjustments(minBase);
    const eurMax = adjustments(maxBase);
    return { band: via.band, min: eurMin, max: eurMax };
  }, [
    zona,
    via.band,
    arredo,
    semiterrato,
    oltre3senzascensore,
    attico,
    ecoPerc,
    sisma,
    ener,
    nearUni,
    tipoContratto,
    durataOrd,
    incTrans,
    durStudenti,
    garanzia,
  ]);
  const canoneMensileRange = useMemo(() => ({ min: eurMqRange.min * mq, max: eurMqRange.max * mq }), [eurMqRange, mq]);

  const afterAdjustments = useMemo(() => {
    const adj = cfg.rules.adjustments;
    let band = via.band;
    let eur = via.val;

    // Furnishing (arredo)
    if (arredo === "parziale") {
      if (band === "A") {
        eur *= adj.furnishing.partial.if_band_A.multiplier ?? 1;
      } else {
        band = adj.furnishing.partial.if_band_not_A.promote_to ?? band;
        eur *= adj.furnishing.partial.if_band_not_A.multiplier ?? 1;
      }
    } else if (arredo === "totale") {
      if (band === "A") {
        eur *= adj.furnishing.total.if_band_A.multiplier ?? 1;
      } else {
        band = adj.furnishing.total.if_band_not_A.promote_to ?? band;
        eur *= adj.furnishing.total.if_band_not_A.multiplier ?? 1;
      }
    }

    if (semiterrato) eur *= adj.semi_basement.multiplier ?? 1;
    if (oltre3senzascensore) eur *= adj.top_floor_no_elevator_over_3rd.multiplier ?? 1;
    if (attico) eur *= adj.attic.multiplier ?? 1;

    // Faithful to original HTML: eco is a direct +% (0/5/10)
    eur *= 1 + ecoPerc / 100;

    if (sisma) eur *= adj.seismic_bonus.multiplier ?? 1;

    if (ener !== "none") {
      eur *= 1 + (adj.energy_class as any)[ener];
    }

    if (nearUni) {
      eur *= adj["students_near_university_≤800m"].multiplier ?? 1;
    }

    return { band, eur };
  }, [
    cfg.rules.adjustments,
    via.band,
    via.val,
    arredo,
    semiterrato,
    oltre3senzascensore,
    attico,
    ecoPerc,
    sisma,
    ener,
    nearUni,
  ]);

  const eurMqFinal = useMemo(() => {
    const type = tipoContratto;
    const cmod = cfg.rules.contract_modifiers;
    let e = afterAdjustments.eur;

    if (type === "ordinario") {
      const delta = cmod.ordinario.duration_years[durataOrd] ?? 0;
      e *= 1 + delta;
    } else if (type === "transitorio") {
      const inc = clamp(incTrans, 0, cmod.transitorio.max_increase_percent ?? 0);
      e *= 1 + inc / 100;
    } else if (type === "studenti") {
      const delta = cmod.studenti.duration_months[durStudenti as "≥24" | "≥36"] ?? 0;
      e *= 1 + delta;
    }

    if (garanzia) {
      const red = cfg.rules.guarantee_reduction["third_party_guarantee_months_≥12"] ?? 0;
      e *= 1 + red;
    }

    return e;
  }, [
    afterAdjustments.eur,
    cfg.rules.contract_modifiers,
    cfg.rules.guarantee_reduction,
    durataOrd,
    garanzia,
    incTrans,
    tipoContratto,
    durStudenti,
  ]);

  const canoneMensile = useMemo(() => eurMqFinal * mq, [eurMqFinal, mq]);

  // When switching contract type, keep the conditional controls sensible
  useEffect(() => {
    if (tipoContratto !== "transitorio") setIncTrans(0);
    if (tipoContratto !== "studenti") setDurStudenti("0");
  }, [tipoContratto]);

  // ===================== Render ===================== //
  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit({
        fascia: afterAdjustments.band,
        summary: afterAdjustments.eur.toFixed(2),
        minPrice: fmtEuro(eurMqRange.min),
        maxPrice: fmtEuro(eurMqRange.max),
        canoneMensileMax: fmtEuro(canoneMensileRange.max),
        canoneMensileMin: fmtEuro(canoneMensileRange.min),
      });
    }
  };
  return (
    <Container>
      <div className="simulatore-root">
        <h1 id="app-title">{cfg.meta?.title || "Simulatore"}</h1>
        {/* <div className="simulatore-muted" id="app-meta">
          {(cfg.meta?.version || "") + (cfg.meta?.currency ? ` • ${cfg.meta.currency}` : "")}
        </div> */}
        {/* 1) Dati di base */}
        <fieldset className="simulatore-fieldset">
          <legend className="simulatore-legend">1) Dati di base</legend>
          <div className="simulatore-row">
            <div className="simulatore-col">
              <label className="simulatore-label">
                Zona omogenea
                <select
                  id="zona"
                  value={zonaId !== 0 ? zonaId : 0}
                  onChange={(e) => setZonaId(parseInt(e.target.value, 10))}
                >
                  <option value="">-- Seleziona una zona --</option>
                  {cfg.zones.map((zn) => (
                    <option key={zn.id} value={zn.id}>
                      {zn.id} – {zn.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="simulatore-muted" id="bandRanges">
                Fasce €/mq (A: {zona?.bands?.A?.[0] || 0}–{zona?.bands?.A?.[1] || 0} • B: {zona?.bands?.B?.[0] || 0}–
                {zona?.bands?.B?.[1] || 0} • C: {zona?.bands?.C?.[0] || 0}–{zona?.bands?.C?.[1] || 0})
              </div>
            </div>

            <div className="simulatore-col">
              <label className="simulatore-label">
                Superficie convenzionale (mq)
                <input
                  type="number"
                  className="simulatore-num"
                  min={0}
                  step={0.1}
                  value={mq}
                  onChange={(e) => setMq(parseFloat(e.target.value || "0"))}
                  style={{ width: "100%", height: "1.6em" }}
                />
              </label>
              <div className="simulatore-muted">Inserisci qui il totale della superficie convenzionale già calcolata.</div>
            </div>

            <div className="simulatore-col">
              <label className="simulatore-label">
                Tipologia contratto
                <select id="tipoContratto" value={tipoContratto} onChange={(e) => setTipoContratto(e.target.value as any)}>
                  <option value="">-- Seleziona una tipologia di contratto --</option>
                  <option value="ordinario">Agevolato (esigenze ordinarie)</option>
                  <option value="transitorio">Transitorio</option>
                  <option value="studenti">Studenti universitari</option>
                </select>
              </label>
            </div>
          </div>
        </fieldset>

        {/* 2) Requisiti minimi & Parametri */}
        <fieldset className="simulatore-fieldset">
          <legend className="simulatore-legend">2) Requisiti minimi & Parametri</legend>
          <div className="simulatore-row">
            {/* Prima colonna: Requisiti minimi + Risultati */}
            <div className="simulatore-col">
              <strong>Requisiti minimi (“alloggio normale”)</strong>
              <div id="minReq">
                {cfg.rules.min_requirements.items.map((text, idx) => (
                  <label className="simulatore-label" key={idx} title={cfg.ui.min_requirements_help[idx] || ""}>
                    <input
                      type="checkbox"
                      className="minreq"
                      checked={minReqChecked[idx]}
                      onChange={(e) => {
                        const next = [...minReqChecked];
                        next[idx] = e.target.checked;
                        setMinReqChecked(next);
                      }}
                    />{" "}
                    {text}
                  </label>
                ))}
              </div>
              <div className="simulatore-muted">
                Se un requisito minimo manca → l’alloggio è “non normale” (fascia base C).
              </div>
            </div>

            {/* Seconda colonna: Parametri divisa in due */}
            <div className="simulatore-col">
              <strong>Parametri (seleziona quelli presenti)</strong>
              <div id="params" className="simulatore-grid-2col">
                {cfg.params.map((name, i) => {
                  const description = cfg.ui.help[name] || "";
                  return (
                    <div key={i} style={{ marginBottom: "12px" }}>
                      <label className="simulatore-label" title={description}>
                        <input
                          type="checkbox"
                          className="param"
                          checked={paramsChecked[i]}
                          onChange={(e) => {
                            const next = [...paramsChecked];
                            next[i] = e.target.checked;
                            setParamsChecked(next);
                          }}
                        />{" "}
                        {name}
                      </label>
                      {description && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "grey",
                            marginLeft: "24px",
                          }}
                        >
                          {description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Spostata qui la sezione dei risultati */}
          <div className="simulatore-out" style={{ marginTop: "20px" }}>
            <div>
              Parametri selezionati:{" "}
              <span id="pCount" className="simulatore-badge">
                {paramsCount}
              </span>
            </div>
            <div>
              Alloggio{" "}
              <span id="tipoAlloggio" className="simulatore-badge">
                {isNormal ? "normale" : "non normale"}
              </span>
            </div>
            <div>
              Fascia iniziale/VIA:{" "}
              <span id="viaBand" className="simulatore-pill">
                {via.band}
              </span>{" "}
              €/mq{" "}
              <span id="viaVal" className="simulatore-pill">
                {via.val.toFixed(2)}
              </span>
            </div>
          </div>
          <div className="simulatore-note simulatore-hl">
            Sintesi: alloggio normale → posizionamento iniziale (VIA) secondo conteggio parametri.
            <br /> Se “non normale” → fascia C con posizionamento guidato dai parametri.
          </div>
        </fieldset>
        {/* 3) Aggiustamenti su VIA (VINT) */}
        <fieldset className="simulatore-fieldset">
          <legend className="simulatore-legend">3) Aggiustamenti su VIA (VINT)</legend>
          <div className="simulatore-grid">
            <label className="simulatore-label">
              Arredo
              <select value={arredo} onChange={(e) => setArredo(e.target.value as any)}>
                <option value="nessuno">Nessuno</option>
                <option value="parziale">Parziale</option>
                <option value="totale">Totale</option>
              </select>
            </label>
            <label className="simulatore-label">
              <input type="checkbox" checked={semiterrato} onChange={(e) => setSemiterrato(e.target.checked)} /> Piano
              seminterrato
            </label>
            <label className="simulatore-label">
              <input
                type="checkbox"
                checked={oltre3senzascensore}
                onChange={(e) => setOltre3senzascensore(e.target.checked)}
              />{" "}
              Oltre il 3° piano senza ascensore
            </label>
            <label className="simulatore-label">
              <input type="checkbox" checked={attico} onChange={(e) => setAttico(e.target.checked)} /> Attico
            </label>
            <label className="simulatore-label">
              Eco-upgrade :{/* Faithful to original values 0/5/10 */}
              <select value={ecoPerc} onChange={(e) => setEcoPerc(parseInt(e.target.value, 10) as 0 | 5 | 10)}>
                <option value={0}>Nessuno</option>
                <option value={5}>Uno (+5%)</option>
                <option value={10}>Due o più (+10%)</option>
              </select>
            </label>
            <label className="simulatore-label">
              <input type="checkbox" checked={sisma} onChange={(e) => setSisma(e.target.checked)} /> Interventi Sisma-bonus
            </label>
            <label className="simulatore-label">
              Classe energetica
              <select value={ener} onChange={(e) => setEner(e.target.value as any)}>
                <option value="none">Non specificata</option>
                <option value="D_E_F">D/E/F</option>
                <option value="B_C">B/C</option>
                <option value="A_or_higher">A o superiore</option>
              </select>
            </label>
            {tipoContratto === "studenti" && (
              <label className="simulatore-label">
                <input type="checkbox" checked={nearUni} onChange={(e) => setNearUni(e.target.checked)} /> Studenti: entro
                800 m dalla sede
              </label>
            )}
          </div>
        </fieldset>
        {/* 4) Maggiorazioni/Riduzioni per tipologia contrattuale */}
        <fieldset className="simulatore-fieldset" id="contrattoBox">
          <legend className="simulatore-legend">4) Maggiorazioni/Riduzioni per tipologia contrattuale</legend>

          {tipoContratto === "ordinario" && (
            <div className="simulatore-grid" id="ordinarioBox">
              <label className="simulatore-label">
                Durata (anni) – solo ordinari
                <select value={durataOrd} onChange={(e) => setDurataOrd(e.target.value as any)}>
                  <option value="3">3 (base)</option>
                  <option value="4">4 (+5%)</option>
                  <option value="5">5 (+7%)</option>
                  <option value="6_or_more">6 o più (+12%)</option>
                </select>
              </label>
            </div>
          )}

          {tipoContratto === "transitorio" && (
            <div className="simulatore-grid" id="transitorioBox">
              <label className="simulatore-label">
                Aumento ammesso (fino a +{cfg.rules.contract_modifiers.transitorio.max_increase_percent}%)
                <input
                  type="number"
                  min={0}
                  max={cfg.rules.contract_modifiers.transitorio.max_increase_percent}
                  step={1}
                  value={incTrans}
                  onChange={(e) =>
                    setIncTrans(
                      clamp(
                        parseFloat(e.target.value || "0"),
                        0,
                        cfg.rules.contract_modifiers.transitorio.max_increase_percent
                      )
                    )
                  }
                />{" "}
                %
              </label>
            </div>
          )}

          {tipoContratto === "studenti" && (
            <div className="simulatore-grid" id="studentiBox">
              <label className="simulatore-label">
                Durata studenti
                <select value={durStudenti} onChange={(e) => setDurStudenti(e.target.value as any)}>
                  <option value="0">6–23 mesi (base)</option>
                  <option value="≥24">≥ 24 mesi (+4%)</option>
                  <option value="≥36">≥ 36 mesi (+6%)</option>
                </select>
              </label>
            </div>
          )}

          <div className="simulatore-grid">
            <label className="simulatore-label">
              <input type="checkbox" checked={garanzia} onChange={(e) => setGaranzia(e.target.checked)} /> Garanzia ≥ 12
              mensilità (−4%)
            </label>
          </div>
        </fieldset>
        {/* 5) Risultato */}
        <fieldset className="simulatore-fieldset">
          {" "}
          <legend className="simulatore-legend">5) Risultato</legend>{" "}
          <div className="simulatore-out">
            {" "}
            <div>
              {" "}
              Fascia: <span className="simulatore-pill">{eurMqRange.band}</span>{" "}
            </div>{" "}
            <div>
              {" "}
              €/mq finale (min–max):{" "}
              <strong>
                {fmtEuro(eurMqRange.min)} – {fmtEuro(eurMqRange.max)}
              </strong>{" "}
            </div>{" "}
            <div>
              {" "}
              Canone mensile (min–max):{" "}
              <strong>
                {fmtEuro(canoneMensileRange.min)} € – {fmtEuro(canoneMensileRange.max)} € / mese
              </strong>{" "}
            </div>{" "}
            <small> Il range è calcolato applicando i correttivi a entrambi gli estremi della fascia selezionata.</small>
          </div>{" "}
          <button className="simulatore-btn mt-2" onClick={handleSubmit}>
            {" "}
            Ricalcola{" "}
          </button>{" "}
        </fieldset>
      </div>
    </Container>
  );
};

export default BariCanoneSimulator;
